/**
 * Save system: serialize gameState to localStorage under a versioned key.
 * Includes migration hooks so old saves survive state-shape changes.
 */

import { SAVE_VERSION, createInitialState } from './gameState.js';

const SAVE_KEY = 'pocketdungeon.save';

/**
 * Migrations keyed by the version they upgrade FROM.
 * Each returns the state bumped one version forward.
 * @type {Record<number, (state:object)=>object>}
 */
const MIGRATIONS = {
  // v1 -> v2: add consumables + death lockout introduced in Phase 3.
  1: (state) => {
    if (!state.consumables) state.consumables = { foods: {}, reviveKits: 0 };
    if (state.lockedUntil == null) state.lockedUntil = 0;
    state.saveVersion = 2;
    return state;
  },
  // v2 -> v3: hero selection. Existing saves already picked, so mark chosen.
  2: (state) => {
    if (state.heroChosen == null) state.heroChosen = true;
    state.saveVersion = 3;
    return state;
  },
  // v3 -> v4: skill tree fields on each hero.
  3: (state) => {
    for (const hero of (state.party ?? [])) {
      if (!hero.skills) hero.skills = [];
      if (hero.skillPoints == null) hero.skillPoints = Math.max(0, hero.level - 1);
    }
    state.saveVersion = 4;
    return state;
  },
  // v4 -> v5: per-class hero progression. The current hero keeps its progress;
  // other classes start fresh at level 1 the first time they're selected.
  4: (state) => {
    if (!state.heroStash) state.heroStash = {};
    state.saveVersion = 5;
    return state;
  },
  // v5 -> v6: party duo + multi-monster + per-hero equipment/lockout, plus a
  // deliberate full reset to re-baseline progression (gold ÷10, drop −20%).
  // The shapes changed enough that the cleanest, intended path is a fresh start
  // from zero — levels, xp, skills, items, consumables and gold all wiped.
  5: () => createInitialState(),
  // v6 -> v7: full reset requested by design — every character back to level 1,
  // no items, no skills, no consumables, no gold (rebalance: drop/gold/exp cut).
  6: () => createInitialState(),
  // v7 -> v8: fresh reset for a clean test pass — everyone back to level 1, no
  // items/skills/gold, alongside the harsher drop rate and slower xp/gold.
  7: () => createInitialState(),
  // v8 -> v9: another full reset so the new Green Meadows sprites + attack
  // animations and the 2× strawberry drop can be tested from level 1.
  8: () => createInitialState(),
  // v9 -> v10: clean-slate reset before the refinement pass (fresh playtest).
  9: () => createInitialState(),
  // v10 -> v11: full reset (gold/items/levels) to playtest balance from zero.
  10: () => createInitialState(),
  // v11 -> v12: full reset to retest the easier map 1 + hero-swap fix.
  11: () => createInitialState(),
  // v12 -> v13: full reset to test the bird mob, grounded rat, 2× fruit drop,
  // the consumables shop "+" button and the closable hero-select.
  12: () => createInitialState(),
  // v13 -> v14: full reset for the balance pass — fixed mag/res being dead
  // stats, solo pack size scaling, crit/dodge/etc caps, death lockout 6h->1h,
  // and a formula-driven monster hp/atk recalibration for tiers 2-14.
  13: () => createInitialState(),
  // v14 -> v15: full reset to add the daily dungeon (Tier 3+, one attempt/day,
  // guaranteed rare+ loot) and the Protection Amulet consumable.
  14: () => createInitialState(),
  // v15 -> v16: SEASON WIPE — everyone back to level 1 for the new ruleset:
  // no offline progress (the dungeon only fights while the page/PiP is open),
  // in-zone monster scaling, harder economy and the gear power-curve fix.
  // Old cloud saves are also v15, so they get discarded by this same migration
  // if they're ever adopted; the KV save:/lb: keys were wiped server-side too.
  15: () => createInitialState(),
};

/**
 * Persist the given state to localStorage.
 * @param {object} state
 */
export function saveGame(state) {
  state.lastSavedAt = Date.now();
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(state));
  } catch (err) {
    console.error('PocketDungeon: failed to save', err);
  }
}

/**
 * Load state from localStorage, running migrations as needed.
 * Returns a fresh initial state if nothing is saved or the save is corrupt.
 * @returns {object}
 */
export function loadGame() {
  let raw;
  try {
    raw = localStorage.getItem(SAVE_KEY);
  } catch (err) {
    console.error('PocketDungeon: failed to read save', err);
    return createInitialState();
  }
  if (!raw) return createInitialState();

  let state;
  try {
    state = JSON.parse(raw);
  } catch {
    console.warn('PocketDungeon: corrupt save, starting fresh');
    return createInitialState();
  }

  return migrate(state);
}

/**
 * Run pending migrations until the state matches SAVE_VERSION.
 * @param {object} state
 * @returns {object}
 */
export function migrate(state) {
  let version = state.saveVersion ?? 0;
  while (version < SAVE_VERSION && MIGRATIONS[version]) {
    state = MIGRATIONS[version](state);
    version = state.saveVersion;
  }
  return state;
}

/** Remove the save (for debugging / new game). */
export function clearSave() {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch (err) {
    console.error('PocketDungeon: failed to clear save', err);
  }
}
